import { ReactNode } from "react";
import { SP } from "../http/serviceportalApi";
import { VV } from "../http/vertragsverwaltungApi";
import { Permission } from "../types";

const i18n = {
  title: "Portale servizi HGV",
  loading: "Caricamento in corso …",
  notAvailable: "N / D",
  footer: {
    companyName: "Unione Albergatori e Pubblici Esercenti (HGV)",
    street: "Via Macello 59",
    city: "39100 Bolzano",
  },
  notifications: {
    saveSuccess: "Dati salvati con successo.",
    saveError: "Errore durante il salvataggio dei dati.",
  },
  appOpenError: "Errore durante l’apertura dell’app.",
  home: {
    title: "Benvenuto/a nel portale servizi HGV",
    description:
      "Nell’Area Clienti HGV trova offerte di servizi e prestazioni esclusive riservate ai clienti dei servizi HGV. Inoltre, può consultare in qualsiasi momento le elaborazioni paghe, stampare contratti di lavoro personalizzati, analizzare l’andamento del fatturato e dei costi, visualizzare tutti gli indicatori aziendali della Sua impresa e molto altro ancora.",
    recentApps: "Le Sue applicazioni utilizzate di recente",
    recentAppsEmpty:
      "Qui sono visualizzate le applicazioni utilizzate di recente.",
  },
  accountButton: {
    language: "Lingua",
    account: "Account",
    invites: "Inviti",
    logout: "Esci",
  },
  menu: {
    clientPortal: "Portale per clienti",
    economicPortal: "Portale per consulenti economici",
    privatePortal: "Portale per privati",
    auditPortal: "Portale per revisori",
    admin: "Amministrazione",
  },
  invites: {
    title: "Inviti",
    columnHeaders: {
      member: "Socio",
      companies: "Sedi operative",
      validUntil: "Valido fino al",
      email: "Indirizzo email",
      status: "Stato",
    },
    accept: "Accettare l’invito",
    decline: "Rifiutare l’invito",
    status: {
      declined: "Rifiutato",
      pending: "Pendente",
      expired: "Scaduto",
    },
  },
  nav: {
    home: "Home",
    myBusiness: "La mia azienda",
    services: "Servizi",
    quickAccess: "Accesso rapido",
    apps: "Applicazioni",
  },
  joinFutureHospitality: {
    title: "Perché i collaboratori sono i tuoi nuovi ospiti",
    paragraph1:
      "Con JOIN future hospitality rendiamo insieme l’Alto Adige una destinazione d’eccellenza per i talenti della gastronomia e dell’ospitalità! Preparati al futuro e inizia subito il Readiness-Check: il tuo primo passo verso Future Hospitality.",
    paragraph2: "Scopri a che punto sei come datore di lavoro.",
    notInterested: "Nessun interesse",
    readinessCheck: "Inizia il Readiness-Check",
  },
  memberSelect: {
    loadError: "Errore durante il caricamento del socio",
    searchPlaceholder: "Cerca soci …",
    groupTitle: "Soci",
    noResults: (query: string) => (
      <>
        Non sono stati trovati soci – <strong>{query}</strong> – corrispondenti
        ai criteri di ricerca.
      </>
    ),
    empty: "Nessun socio trovato.",
    loadMembersError: "Errore durante il caricamento dei soci",
  },
  errorPage: {
    apology: "Siamo spiacenti",
    unexpectedError:
      "Si è verificato un errore imprevisto. La preghiamo di riprovare tra qualche istante.",
    contactHgv:
      "Qualora il problema dovesse persistere, La preghiamo di contattare il Suo referente HGV.",
    altTextError: "Errore",
  },
  table: {
    outOf: "di",
    defaultSearchPlaceholder: "Ricerca",
    noItems: "Nessun elemento trovato.",
    loadError: "Errore durante il caricamento degli elementi",
    prevPage: (pageSize: number) => `${pageSize} righe precedenti`,
    nextPage: (pageSize: number) => `${pageSize} righe successive`,
  },
  formField: {
    save: "Salva",
    optional: "(opzionale)",
    errors: {
      required: "È necessario inserire un valore nel campo.",
      min: "Il campo non contiene un valore valido. Inserisca un valore maggiore.",
      max: "Il campo non contiene un valore valido. Inserisca un valore minore.",
      pattern: "Il valore ha un formato non valido.",
      validate: "Valore non valido.",
    },
  },
  departments: {
    accounting: {
      title: "Consulenza fiscale",
      description:
        "Il reparto consulenza fiscale dell’HGV è il Suo punto di riferimento per tutte le questioni fiscali nel settore alberghiero e della ristorazione. Con competenza, affidabilità e professionalità ci occupiamo di tutti gli adempimenti e delle pratiche fiscali della Sua azienda.",
      apps: {
        tagesinkasso: {
          title: "Registro degli incassi giornalieri",
          description:
            "Serve per la registrazione obbligatoria degli incassi giornalieri e, in caso di contabilità ordinaria, anche per la registrazione dei movimenti di cassa in contanti.",
        },
        monatsdaten: {
          title: "Dati mensili",
          description:
            "Serve per la rilevazione mensile dei dati strutturali dell’azienda, quali numero di posti letto, pernottamenti, posti a sedere e giorni di apertura.",
        },
        bilanzOnline: {
          title: "Indicatori",
          description:
            "Serve per la consultazione di indicatori fiscali e civilistici dell’azienda.",
        },
        businessCheck: {
          title: "Business Check",
          description:
            "Consente di monitorare costantemente l’andamento aziendale tramite dati di bilancio aggiornati, grafici e confronti annuali.",
        },
        openItems: {
          title: "Partite aperte",
          description:
            "Fornisce una panoramica aggiornata di tutte le partite aperte.",
        },
        invoicePortal: {
          title: "Portale fatture",
          description:
            "Consente di creare e gestire in modo semplice tutte le fatture in entrata e in uscita.",
        },
        endbestaende: {
          title: "Rimanenze finali",
          description:
            "Consente la rilevazione dettagliata delle rimanenze finali di merci e beni alla fine dell’anno.",
        },
      },
    },
    joinHospitality: {
      title: "JOIN hospitality",
      description:
        "JOIN hospitality vi aiuta a diventare un datore di lavoro attrattivo, rafforzando la ricerca e la fidelizzazione di collaboratori e collaboratrici. Accompagniamo i soci dell’HGV con un Readiness-Check e un Pre-Coaching gratuiti, consulenze personalizzate e strumenti concreti per l’organizzazione, la gestione del personale e il recruiting.",
      backLink: "Torna a JOIN hospitality",
      apps: {
        readinessCheck: {
          title: "Readiness-Check",
          description:
            "Quanto è davvero attraente come datore di lavoro? Il Readiness-Check gratuito Le mostra a che punto si trova e fornisce spunti concreti e raccomandazioni per il Suo sviluppo futuro. Attualmente disponibile solo in lingua tedesca.",
        },
        joinToolbox: {
          title: "JOIN Toolbox",
          description:
            "Assumere, formare e fidelizzare i collaboratori e le collaboratrici: qui troverà informazioni, checklist e guide per ogni fase della collaborazione, dalla ricerca fino alla permanenza in azienda a lungo termine. Attualmente disponibile solo in lingua tedesca.",
        },
        joinJobOffers: {
          title: "Offerte di lavoro sul sito web di JOIN",
          description:
            "Pubblichi le Sue offerte di lavoro direttamente sul sito web di JOIN hospitality. Qui troverà una guida passo passo per la pubblicazione.",
        },
        continuingEducation: {
          title: "Formazione HGV",
          description:
            "L’HGV offre un ricco programma di corsi per tutte le persone attive nel settore alberghiero e della ristorazione.",
          url: "https://www.hgv.it/it/servizi/formazione",
        },
        consultingAndCoaching: {
          title: "Consulenza e coaching",
          description:
            "La aiutiamo a migliorare le Sue competenze nella gestione del personale. Grazie ai nostri servizi di consulenza potrà attrarre e fidelizzare collaboratori e collaboratrici eccellenti e, grazie al nostro coaching per datori di lavoro, potrà sviluppare costantemente le Sue competenze manageriali. Sito attualmente disponibile solo in lingua tedesca.",
        },
      },
    },
    payroll: {
      title: "Consulenza del personale",
      description:
        "Il reparto consulenza del personale dell’HGV è il Suo partner affidabile e competente per tutte le questioni relative ai collaboratori. Qui può accedere in modo rapido e semplice a tutti i documenti e alle applicazioni necessari per la gestione del personale.",
      apps: {
        arbeitssicherheit: {
          title: "Sicurezza sul lavoro per i dipendenti",
          description:
            "Iscrizione al corso obbligatorio sulla sicurezza sul lavoro per i collaboratori e le collaboratrici.",
        },
        hourRegistration: {
          title: "Registro delle ore",
          description:
            "Inserimento e gestione semplice delle ore di lavoro dei collaboratori. Non è più necessario stampare e archiviare i registri delle ore.",
        },
        employeeRegistration: {
          title: "Registrazione del dipendente",
          description: (
            <>
              Per garantire la comunicazione tempestiva all’Ufficio del Lavoro e
              la predisposizione del contratto di lavoro, è necessario
              trasmettere tutte le informazioni complete{" "}
              <strong>entro e non oltre le ore 11:45</strong> dell’ultimo giorno
              lavorativo precedente l’inizio del rapporto di lavoro.
            </>
          ),
        },
        urgentEmployeeRegistration: {
          title: "Registrazione urgente del dipendente",
          description:
            "Registrazione urgente del dipendente al di fuori degli orari di apertura.",
        },
        emergencyService: {
          title: "Servizio di emergenza",
          description:
            "Raccolta delle domande più frequenti (FAQ) con relative risposte. Qualora le FAQ disponibili non consentano di risolvere il Suo caso specifico, è possibile attivare un servizio di reperibilità personalizzato.",
        },
        arbeitAufAbruf: {
          title: "Lavoro a chiamata – Comunicazione preventiva",
          description:
            "Per ogni lavoratore o lavoratrice assunto/a con contratto di lavoro intermittente, prima di ogni prestazione lavorativa deve essere effettuata un’ulteriore comunicazione al Ministero del Lavoro.",
        },
      },
    },
    webMarketing: {
      title: "Web Marketing",
      description: (link: ReactNode) => (
        <>
          Il reparto Web Marketing offre consulenza ai clienti in materia di
          marketing online e prenotazioni online. Inoltre, attraverso lo
          sviluppo di diversi strumenti e portali, si impegna costantemente a
          semplificare l’organizzazione aziendale e a supportare al meglio la
          Sua presenza sul web. Una panoramica dei nostri prodotti e servizi è
          disponibile sulla nostra {link}.
        </>
      ),
      descriptionLinkLabel: "pagina demo",
      apps: {
        dac7: {
          title: "Obbligo di dichiarazione DAC7",
          description: (
            <>
              A seguito del Decreto Legislativo n. 32/2023, che recepisce in
              Italia la direttiva europea “DAC7”, i gestori di piattaforme
              digitali sono tenuti a comunicare alle autorità fiscali europee i
              dati e le transazioni dei venditori registrati sulla piattaforma.
            </>
          ),
        },
      },
    },
  },
  services: {
    open: "Apri",
    new: "Nuovo",
  },
  allCompanies: "Tutte le sedi operative",
  companies: (count: number) =>
    `${count} ${count === 1 ? "sede operativa" : "sedi operative"}`,
  quickAccessTypes: {
    documents: "Documenti",
  },
  documentSet: {
    lohnauswertung: "Elaborazione paghe",
    "zahlungsbestaetigung-f24": "Conferma di pagamento F24",
    einheitslohnbuch: "Libro Unico del Lavoro",
    "formblatt-770": "Modulo 770",
    "formblatt-cu": "Modulo CU",
    matrikelbuch: "Libro matricola",
    arbeitsvertraege: "Contratti di lavoro",
    "lohn-rundschreiben": "Circolare",
    "steuer-rundschreiben": "Circolare",
    "prov-lohnstreifen": "Cedolino provvisorio",
    "zusaetzliche-auswertungen": "Elaborazioni aggiuntive",
    jahresdokumente: "Documenti annuali",
    "antrag-um-familiengeld": "Richiesta di assegno per il nucleo familiare",
    jahresauswertungen: "Elaborazioni annuali",
  },
  getAgreementStatus: (status: VV.AgreementStatus) => {
    switch (status) {
      case "Published":
        return "Aperto";
      case "Signed":
        return "Confermato";
      case "Cancelled":
        return "Risolto";
      default:
        return i18n.notAvailable;
    }
  },
  getPermission: (p: Permission): string => {
    switch (p) {
      case Permission.UsersRead:
        return "Visualizzare utenti";
      case Permission.UsersWrite:
        return "Modificare utenti";
      case Permission.InvitesRead:
        return "Visualizzare inviti";
      case Permission.MemberUsersRead:
        return "Visualizzare gli utenti associati";
      case Permission.MemberUsersWrite:
        return "Modificare gli utenti associati";
      case Permission.AccountingsRead:
        return "Indicatori";
      case Permission.OpenItemsRead:
        return "Partite aperte";
      case Permission.WageCostsRead:
        return "Indicatori";
      case Permission.HourRegistrationExecute:
        return "Registro delle ore";
      case Permission.EmployeeRegistrationExecute:
        return "Comunicazione di assunzione";
      case Permission.InvoicePortalExecute:
        return "Portale fatture SEAC";
      case Permission.AgreementsExecute:
        return "Amministrazione dei contratti";
      case Permission.BusinessCheckExecute:
        return "Business Check";
      case Permission.TIBuchRead:
        return "Registro degli incassi giornalieri";
      case Permission.MonatsdatenDataWrite:
        return "Dati mensili";
      case Permission.MonatsdatenSettingsWrite:
        return "Dati mensili admin";
      case Permission.EndbestaendeWorksheetsWrite:
        return "Rimanenze finali";
      case Permission.EndbestaendeSettingsWrite:
        return "Rimanenze finali admin";
      case Permission.JoinHospitalityLevel1Read:
        return "JOIN hospitality Level 1";
      case Permission.JoinHospitalityLevel2Read:
        return "JOIN hospitality Level 2";
      case Permission.Dac7Write:
        return "Obbligo di dichiarazione DAC7";
      case Permission.DocumentsSteuerRundschreibenRead:
        return "Circolare";
      case Permission.DocumentsJahresauswertungRead:
        return "Elaborazione annuale";
      case Permission.DocumentsRevisorenRead:
        return "Documenti del revisore";
      case Permission.DocumentsBetriebOnlineServiceRead:
        return "Servizio online aziendale";
      case Permission.DocumentsBetriebStandardRead:
        return "Azienda standard";
      case Permission.DocumentsPrivateHaushalteRead:
        return "Nuclei familiari privati";
      case Permission.DocumentsWirtschaftsberaterRead:
        return "Commercialista";
      case Permission.DocumentsAufstellungNettoloehneRead:
        return "Riepilogo dei salari netti";
      case Permission.DocumentsBuchungsbelegeRead:
        return "Documenti contabili";
      case Permission.DocumentsCUDRead:
        return "CUD";
      case Permission.DocumentsEinbehalt15Read:
        return "Trattenuta del quinto";
      case Permission.DocumentsErklSteuerfreibetraegeRead:
        return "Dichiarazione delle detrazioni fiscali";
      case Permission.DocumentsLaborfondMonatlichRead:
        return "Laborfonds mensile";
      case Permission.DocumentsLaborfondTrimestralRead:
        return "Laborfonds trimestrale";
      case Permission.DocumentsLohnstreifenRead:
        return "Cedolino";
      case Permission.DocumentsMatrikelbuchRead:
        return "Libro matricola";
      case Permission.DocumentsF24Read:
        return "F24";
      case Permission.DocumentsNummInailRead:
        return "Numm. INAIL";
      case Permission.DocumentsSchulgeldLehrlingeRead:
        return "Contributo scolastico degli apprendisti";
      case Permission.DocumentsUeberweisungslisteRead:
        return "Elenco dei bonifici";
      case Permission.DocumentsZahlungsbestaetigungF24Read:
        return "Conferma di pagamento F24";
      case Permission.DocumentsEinheitslohnbuchRead:
        return "Libro Unico del Lavoro";
      case Permission.Documents770SemplificatoRead:
        return "770 semplificato";
      case Permission.Documents770OrdinarioRead:
        return "770 ordinario";
      case Permission.DocumentsArbeitsvertraegeRead:
        return "Contratti di lavoro";
      case Permission.DocumentsLohnRundschreibenRead:
        return "Circolari";
      case Permission.DocumentsProvisorischerLohnstreifenRead:
        return "Cedolino provvisorio";
    }
  },
  getDepartment: (d: SP.Department): string => {
    switch (d) {
      case "Steuer":
        return "Consulenza fiscale";
      case "Personal":
        return "Consulenza del personale";
      default:
        return d;
    }
  },
  admin: {
    title: "Portale servizi amministrazione",
    users: {
      title: "Utenti",
      searchPlaceholder: "Cerca utente",
      columnHeaders: {
        name: "Nome",
      },
      deleteModal: {
        title: "È sicuro/a?",
        cancel: "Annulla",
        confirm: "Cancellare",
        body: (email: string) => (
          <>
            È sicuro/a di voler rimuovere <strong>{email}</strong>?
          </>
        ),
      },
    },
    user: {
      backLink: "Torna agli utenti",
      id: "ID",
      tabs: {
        infos: "Informazioni",
        assignments: "Accesso",
      },
      loadError: "Errore nel caricamento dell’utente.",
      infos: {
        name: "Nome",
        email: "Indirizzo email",
      },
    },
    invites: {
      title: "Inviti",
      columnHeaders: {
        email: "Indirizzo email",
        member: "Socio",
        companies: "Sedi aziendali",
        validUntil: "Valido fino al",
        status: "Stato",
      },
    },
    assignments: {
      columnHeaders: {
        member: "Socio",
        companies: "Sedi aziendali",
        company: "Sede aziendale",
        access: "Accesso",
      },
    },
    accounting: {
      title: "Gestione degli associati",
      label: {
        company: "Sede aziendale",
        endDate: "Data di fine",
        member: "Azienda",
        searchMemberPlaceholder: "Cerca azienda",
        status: "Stato",
        year: "Anno",
      },
      worksheet: {
        status: {
          completed: "Terminato",
          in_progress: "In corso",
          not_started: "Non iniziato",
        },
      },
    },
  },
  revisorApp: {
    title: "Portale servizi revisori",
  },
  economicAdvisorApp: {
    title: "Portale servizi commercialisti",
    questionnaire: {
      title: "Modello CU / 770",
      description: {
        paragraph1: "Di seguito trova l’elenco dei nostri clienti comuni.",
        paragraph2: (
          <>
            Per i clienti per i quali ha effettuato l’invio della Certificazione
            Unica (CU) relativa ai lavoratori autonomi, La preghiamo di
            selezionare l’apposita casella di controllo. Indichi inoltre il
            codice fiscale con il quale provvederà all’invio del Mod. 770.{" "}
            <strong>
              Con l’attivazione della casella si assume contestualmente la
              responsabilità dell’invio del Mod. 770 relativo ai lavoratori
              autonomi entro i termini previsti.
            </strong>
          </>
        ),
        paragraph3:
          "L’invio telematico del Mod. 770 (relativo ai lavoratori dipendenti) viene effettuato dalla Cooperativa di servizi HGV con il seguente codice fiscale: 00576540215.",
        paragraph4:
          "Qualora per i clienti sia incaricato un revisore, La preghiamo di indicarne anche i relativi dati.",
      },
      disabled:
        "L’inserimento non è più possibile. La preghiamo di contattare il reparto consulenza del personale dell’HGV.",
      deadlineAlert: (deadline: string) =>
        `La preghiamo di confermare l’inserimento entro il ${deadline} facendo clic su “Conferma”, anche nel caso in cui non abbia selezionato alcuna casella di controllo.`,
      noCustomers: "Nessun elenco clienti disponibile.",
      submit: "Conferma",
      loadError: "Impossibile caricare i dati.",
      columnHeaders: {
        name: "Nome",
        freelanceInvoice: "Fattura di libero professionista",
        revisor: "Revisore",
      },
      freelanceTaxCode: {
        label: "Codice fiscale del libero professionista",
        helpText:
          "Codice fiscale con il quale viene effettuato l’invio del Mod. 770 relativo ai liberi professionisti e ai redditi di capitale.",
        invalidError: "Codice fiscale non valido.",
      },
      revisorFunction: {
        label: "Ruolo di revisore",
        placeholder: "— Selezionare —",
      },
      revisorTaxCode: "Codice fiscale",
      revisorTypes: {
        revisore_contabile: {
          label: "Revisore contabile",
        },
        societa_e_responsabile: {
          label: "Società di revisione & Responsabile per la revisione",
          helpTexts: {
            societa: "Società di revisione",
            responsabile: "Responsabile per la revisione",
          },
        },
        collegio_sindacale: {
          label: "Collegio sindacale",
        },
      },
    },
  },
  privateHouseholdApp: {
    title: "Portale servizi nuclei familiari privati",
  },
  masterData: {
    title: "Dati anagrafici",
    description:
      "I Suoi dati associativi e le informazioni generali sulle Sue sedi operative.",
    name: "Nome",
    street: "Strada",
    city: "Luogo",
    postalCode: "CAP",
    memberData: {
      title: "Dati associativi",
      memberId: "Numero associato",
      taxId: "Codice fiscale",
      vatId: "Partita IVA",
    },
    companies: {
      title: "Sedi aziendali",
      companyId: "Numero della sede aziendale",
    },
  },
  memberSettings: {
    title: "Utenti",
    description:
      "Qui trova un elenco degli utenti che hanno accesso ai dati di una o più delle Sue sedi operative.",
    inviteUser: "Invitare utente",
    tabs: {
      users: "Utenti",
      invites: "Inviti",
    },
    users: {
      columnHeaders: {
        name: "Nome",
        email: "Indirizzo email",
        companies: "Sedi aziendali",
        company: "Sede aziendale",
        access: "Accesso",
      },
      legalRepresentative: "Rappresentante legale",
      confirmRemove: {
        title: "È sicuro/a?",
        cancel: "Annulla",
        remove: "Rimuovere",
        message: (userName: string, companyName: string) => (
          <>
            È sicuro/a di voler rimuovere <strong>{userName}</strong> da{" "}
            <strong>{companyName}</strong>?
          </>
        ),
      },
    },
    editUser: {
      titleNew: "Invitare utente",
      titleEdit: "Modificare utente",
      backLink: "Torna agli utenti",
      namePlaceholder: "Nome",
      emailPlaceholder: "Indirizzo email",
      legalRepresentative: "Rappresentante legale",
      invite: "Invitare",
      panelPermissions: "Diritti di accesso",
      panelApplications: "Applicazioni",
      panelDocuments: "Documenti",
      panelWebdesk: "Webdesk",
      webdeskUsername: "Nome utente",
      inviteSent: "Invito inviato con successo.",
      inviteError: "Errore durante l’invio dell’invito.",
    },
  },
  documents: {
    title: "Documenti",
    maintenanceAlert: (from: string, to: string) =>
      `A causa di lavori di manutenzione, i documenti non saranno consultabili oggi dalle ${from} fino a circa le ${to}.`,
    loadError: "Impossibile caricare i documenti.",
    pdfFallback: (documentLink: string) =>
      `Questo browser non supporta l’anteprima dei file PDF. La preghiamo di scaricare il ${documentLink} per poterlo visualizzare.`,
    download: {
      downloadAll: "Scaricare tutti i risultati",
      formatPrompt: "Selezionare un formato",
      formats: {
        zip: {
          label: "ZIP",
          description:
            "Scaricare tutti i documenti singolarmente in un file ZIP",
        },
        pdf: {
          label: "PDF",
          description: "Scaricare tutti i documenti in un unico file PDF",
        },
        pdfWithoutMyGastroDocx: {
          label: "PDF senza MyGastroDocx",
          description:
            "Scaricare tutti i documenti in un unico file PDF senza i cedolini paga MyGastroDocx",
        },
      },
      started: "Il download è stato avviato.",
      success: "Download completato con successo.",
      error: "Errore durante il download del file.",
    },
    table: {
      columnHeaders: {
        department: "Reparto",
        memberId: "Numero associato",
        customer: "Cliente",
        date: "Data",
        document: "Documento",
        employee: "Dipendente",
        myGastroDocx: "MyGastroDocx",
      },
      openError: "Impossibile aprire il documento.",
      myGastroDocxYes: "Sì",
    },
  },
  bilanzOnline: {
    title: "Indicatori",
    description:
      "Qui può consultare e analizzare in qualsiasi momento i Suoi dati aggiornati, creare periodi di confronto e verificare i Suoi indicatori aziendali.",
    companySelect: "Selezione dell’azienda",
    evaluation: {
      label: "Elaborazione",
      options: {
        competence: "Economico-aziendale",
        fiscal: "Fiscale",
      },
    },
    periodSelect: {
      label: "Periodo",
      from: "Da",
      to: "A",
    },
    wageCostAnalysis: {
      title: "Prospetto dei costi del personale",
      annualComparison: "Riepilogo annuale",
      annualComparisonUntil: "Riepilogo annuale fino a",
      showWholeYear: "Mostra l’intero anno",
      wageCostLabels: {
        wage: "Retribuzione",
        additionalSalaries: "Mensilità aggiuntive",
        otherWage: "Altri elementi retributivi",
        dues: "Contributi",
        abfertigung: "Trattamento di Fine Rapporto (incl. fondi)",
        sum: "Costi salariali",
      },
      otherWageDetail: "Premi / rimborsi spese / ferie residue pagate",
      dataAvailableUntil: (month: string) =>
        `Dati disponibili solo fino a ${month}`,
      monthlyComparison: "Riepilogo mensile",
      month: "Mese",
      columnHeaders: {
        label: "Denominazione",
        difference: "Differenza",
      },
    },
    wageCostCharts: {
      title: "Visualizzazione grafica dei costi del personale",
      breakdownLaborCosts: "Prospetto dei costi del personale",
      showPreviousYear: "Mostra l’anno precedente",
      averageEmployeesPerMonth: "Numero medio di dipendenti per mese",
      employeeFilter: {
        label: "Dipendenti",
        options: {
          all: "Tutti",
          employees: "Dipendenti",
          owner_and_employedfamilymembers: "Soci / membri della famiglia",
        },
      },
      yearsSelect: {
        year: "Anno",
        years: "Anni",
      },
      period: "Periodo",
      workdaysPerMonth: "Giorni lavorativi per mese",
      absentDaysPerMonth: "Giorni di assenza per mese",
    },
    threeYearAnalysis: {
      title: "Analisi contabile triennale",
      showAllRows: "Mostra tutte le righe",
      showWholeYear: "Mostra tutto l’anno",
      showDifferences:
        "Mostra la differenza percentuale rispetto all’anno precedente",
      downloadExcel: "Scaricare il documento Excel",
      columnHeaders: {
        sum: {
          label: "Totale",
          description: "Il totale degli importi",
        },
        percent: {
          label: "Percentuale",
          description: "Incidenza percentuale sul fatturato",
        },
        yearAsSum: {
          label: "Totale annuale",
          description: "Totale calcolato sull’intero anno",
        },
        yearInPercent: {
          label: "Anno in percentuale",
          description: "Percentuale calcolata sull’intero anno",
        },
        difference: {
          label: "Diff.",
          description: "Differenza percentuale rispetto all’anno precedente",
        },
      },
    },
    monthlyAnalysis: {
      title: "Analisi mensile dei dati contabili",
      showAllRows: "Mostra tutto",
      showPreviousYear: "Mostra l’anno precedente",
      downloadExcel: "Scaricare il documento Excel",
      label: "Denominazione",
    },
    accountingCharts: {
      title: "Indicatori contabili",
      showVerwalterEntschaedigung: "Escludi il compenso dell’amministratore",
      noData: "Non sono stati trovati dati per il periodo selezionato.",
      chart1: {
        title:
          "Rappresentazione di fatturato, costo delle merci e costi del personale",
        error:
          "Errore nel caricamento della rappresentazione di fatturato, costo delle merci e costi del personale.",
      },
      chart2: {
        title: "Evoluzione dei costi aziendali",
        error: "Errore nel caricamento dell’evoluzione dei costi aziendali.",
      },
      chart3: {
        title: "Analisi del fatturato mensile",
        error: "Errore nel caricamento dell’analisi del fatturato mensile.",
      },
      chart4: {
        title: "Pernottamenti",
        error: "Errore nel caricamento dei pernottamenti.",
      },
      chart5: {
        title: (chartLabel: string) =>
          `Analisi del fatturato e dei costi operativi per ${chartLabel}`,
        chartLabel: {
          overnightStays: "Pernottamenti adulti",
          seating: "Posti a sedere",
          daysOpen: "Giorni di apertura",
          rentedPitches: "Piazzole affittate",
        },
        selectCompany: "La preghiamo di selezionare un’azienda.",
        error:
          "Impossibile caricare l’analisi del fatturato e dei costi operativi.",
      },
    },
  },
  agreements: {
    title: "Contratti & prezzi",
    description: (
      <>
        Nelle tabelle seguenti trova i listini prezzi attualmente in vigore e i
        contratti che il reparto Web Marketing ha attivato per Lei per
        l’utilizzo di diversi prodotti e servizi. Oltre ai contratti aperti, può
        consultare anche quelli già confermati e disdetti.
      </>
    ),
    hint: "Avviso",
    hintText: {
      paragraph1:
        "Prima di poter visualizzare e confermare i contratti di utilizzo, è necessario leggere e sottoscrivere il contratto quadro. Questa operazione avviene in modalità telematica facendo clic su “Conferma ora”. Il contratto quadro contiene le condizioni generali di contratto, mentre i contratti di utilizzo disciplinano l’impiego dei diversi prodotti e dei relativi servizi. Anche questi ultimi vengono conclusi facendo clic su “Conferma ora”. I relativi costi sono consultabili nel listino prezzi.",
      paragraph2:
        "Dopo la conferma di un contratto, riceverà automaticamente un’email contenente la relativa copia contrattuale.",
      paragraph3:
        "Tutti gli ulteriori dettagli sono disponibili nel contratto quadro e nei relativi contratti di utilizzo.",
      paragraph4:
        "Per ulteriori informazioni, il reparto Web Marketing dell’HGV Bolzano è a Sua completa disposizione!",
    },
    documentsTable: {
      title: "Documenti Web Marketing",
      privacyPolicy: "Informativa sulla privacy",
    },
    agreementsTable: {
      columnHeaders: {
        agreement: "Contratto",
        memberOrCompany: "Socio / Sede aziendale",
        confirmedAt: "Confermato il",
        status: "Stato",
      },
      readAndConfirm: "Leggere e confermare",
      confirmFrameAgreementFirst:
        "La preghiamo di confermare prima il contratto quadro.",
    },
    detail: {
      backLink: "Torna a contratti e prezzi",
      signing: "Il contratto viene confermato …",
      retry: "Riprovare",
      signError:
        "Non è stato possibile confermare il contratto. La preghiamo di riprovare o di contattare il reparto Web Marketing dell’HGV.",
      scrollToConfirm:
        "La preghiamo di scorrere fino in fondo alla pagina per confermare il contratto.",
      acceptTerms:
        "Dichiaro di accettare espressamente le condizioni contrattuali sopra riportate e confermo la sottoscrizione del contratto.",
      acceptVexatoryClauses: {
        intro:
          "Il cliente dichiara espressamente di conoscere e accettare i seguenti articoli del contratto quadro:",
        clauses: {
          clause1:
            "Art. 2 Durata, recesso e disdetta del contratto quadro HGV;",
          clause2:
            "Art. 5 Durata, recesso e disdetta dei singoli contratti di utilizzo;",
          clause3:
            "Art. 6.2 Modifica del listino prezzi relativo ai contratti di utilizzo;",
          clause4:
            "Art. 10 Clausola risolutiva espressa dei contratti di utilizzo;",
          clause5: "Art. 11 Legge applicabile e foro competente;",
        },
      },
      confirm: "Conferma ora",
      loadingAgreement: "Caricamento del contratto …",
    },
  },
  departmentApps: {
    noApps: "Nessuna applicazione attiva.",
    noAppsContact: (emailLink: ReactNode) => (
      <>
        Se desidera una consulenza senza impegno o ricevere maggiori
        informazioni sui nostri prodotti e servizi, La preghiamo di contattarci
        via email all’indirizzo {emailLink}.
      </>
    ),
  },
  configIncomplete:
    "La configurazione dell’azienda non è stata ancora completata.",
  openItems: {
    title: "Partite aperte",
    lastUpdate: (date: string) => `Ultimo aggiornamento: ${date}`,
    loadError: "Impossibile caricare le partite aperte.",
    supplier: "Fornitore",
    customer: "Cliente",
    companiesTable: {
      columnHeaders: {
        name: "Denominazione",
        vatId: "Partita IVA",
        balance: "Saldo",
      },
      total: "Totale",
      unassigned: "<Partite non attribuibili>",
    },
    filters: {
      status: {
        label: "Stato",
        option: {
          all: "Tutti",
          open: "Da saldare",
          settled: "Pagato",
        },
      },
      openUntil: "Da saldare fino al",
      search: {
        label: "Ricerca",
        placeholder: "Denominazione / Partita IVA",
      },
    },
    documentsTable: {
      columnHeaders: {
        invoiceDate: "Data della fattura",
        invoiceNumber: "Numero della fattura",
        documentNumber: "Numero del documento",
        bookingDate: "Data di registrazione",
        credit: "Avere",
        debit: "Dare",
        comment: "Nota",
        title: "Denominazione",
      },
    },
  },
  payrollBackLink: "Torna alla consulenza del personale",
  arbeitssicherheit: {
    title: "Sicurezza sul lavoro per il personale",
    paragraph1:
      "Le disposizioni in materia di sicurezza sul lavoro prevedono che i lavoratori e le lavoratrici frequentino un corso di formazione sulla sicurezza della durata di otto ore (rischio basso). Il corso previsto per il personale del settore alberghiero e della ristorazione è composto da quattro ore di formazione generale e quattro ore di formazione specifica.",
    paragraph2:
      "Ogni cinque anni è obbligatorio frequentare un corso di aggiornamento di sei ore.",
    onlineCourse: {
      linkLabel: "Qui",
      text: "può iscrivere il Suo personale al corso online “sicurezza sul lavoro”.",
    },
    presenceCourse: {
      linkLabel: "Qui",
      text: "può iscrivere il Suo personale a uno dei corsi di aggiornamento in presenza sulla “sicurezza sul lavoro”.",
    },
    asFrom: "Aggiornato ad agosto 2025",
  },
  uniUrg: {
    title: "UniUrg – registrazioni urgenti al di fuori degli orari di apertura",
    paragraph1:
      "In Italia, prima dell’inizio di un rapporto di lavoro subordinato, deve essere effettuata la comunicazione di assunzione mediante il modello UniLav.",
    paragraph2: (
      <>
        Se, per particolari motivi di urgenza, non è possibile effettuare la
        registrazione tramite UniLav il giorno precedente all’inizio
        dell’attività lavorativa (ad esempio perché l’ufficio paghe è chiuso
        durante il fine settimana), è possibile procedere autonomamente a una
        comunicazione provvisoria tramite UniUrg.{" "}
        <strong>
          La comunicazione ordinaria tramite UniLav dovrà comunque essere
          effettuata entro cinque giorni. Pertanto, il reparto consulenza del
          personale dell’HGV competente dovrà essere informato tempestivamente
          via email dell’avvenuta comunicazione UniUrg, trasmettendo tutti i
          dati necessari per la predisposizione del contratto di lavoro.
        </strong>
      </>
    ),
    paragraph3:
      "Limitatamente alla Provincia di Bolzano, a partire dal 4 agosto 2025 la procedura di registrazione sarà modificata e la trasmissione potrà avvenire esclusivamente attraverso le seguenti modalità digitali:",
    option1: "tramite il sistema ProNotel2 oppure",
    option2: (link: ReactNode) => (
      <>in alternativa, tramite il modulo online disponibile qui: {link}</>
    ),
    paragraph4:
      "(ad esempio in caso di problemi tecnici con ProNotel2 oppure in caso di temporanea irreperibilità del consulente del lavoro o dell’ufficio paghe).",
    paragraph5: (email: ReactNode) => (
      <>
        <strong>
          Importante: a partire dalla data sopra indicata, non sarà più
          consentito trasmettere le registrazioni UniUrg all’indirizzo email{" "}
          {email}.
        </strong>
      </>
    ),
  },
  emergencyService: {
    title:
      "Servizio di reperibilità individuale della consulenza del personale HGV",
    introduction: "Cosa fare in caso di:",
    articles: {
      registrations: {
        title: "Registrazioni",
        paragraph1:
          "Si ricorda che tutte le comunicazioni di assunzione devono essere trasmesse al consulente del personale HGV competente entro e non oltre le ore 11:45 dell’ultimo giorno lavorativo precedente l’inizio dell’attività lavorativa, utilizzando la funzione “Comunicazione collaboratore” nel Portale servizi HGV.",
        paragraph2: (link: ReactNode) => (
          <>
            Qualora si renda comunque necessaria un’urgente comunicazione di
            assunzione durante il fine settimana o nei giorni festivi, è
            possibile effettuare la registrazione del collaboratore,{" "}
            <strong>
              entro le ore 24:00 del giorno precedente l’inizio dell’attività
              lavorativa, tramite il {link}
            </strong>{" "}
            e trasmetterla direttamente all’Ufficio del Lavoro.
          </>
        ),
        paragraph2LinkLabel: "modulo online",
        paragraph3: (email: ReactNode) => (
          <>
            La conferma d’invio del modulo UniUrg, nonché il relativo numero di
            protocollo assegnato dall’Ufficio a seguito della trasmissione,
            devono essere inoltrati immediatamente dal cliente al/alla
            rispettivo/a referente della consulenza del personale HGV ({email}),
            affinché la comunicazione ordinaria UniLav e la predisposizione del
            contratto di lavoro possano essere effettuate nel primo giorno
            utile.
          </>
        ),
      },
      disputes: {
        title: "Vertenze e provvedimenti disciplinari",
        paragraph1: (
          <>
            Qualora durante il fine settimana o in un giorno festivo dovesse
            verificarsi una grave controversia (ad esempio aggressione fisica,
            offese o stato di ebbrezza), consigliamo di dispensare il/la{" "}
            <strong>collaboratore/trice</strong> dal servizio per i giorni di
            chiusura della consulenza del personale HGV (ferie, recupero ore
            straordinarie o soluzioni analoghe).
          </>
        ),
        paragraph2: (
          <>
            La preghiamo di contattare il/la Suo/a referente della consulenza
            del personale HGV <strong>il primo giorno lavorativo utile</strong>{" "}
            al fine di discutere la situazione e concordare insieme le ulteriori
            modalità di intervento.
          </>
        ),
      },
      absence: {
        title: "Assenza ingiustificata dal lavoro",
        paragraph1:
          "Qualora un collaboratore si assenti dal posto di lavoro senza un valido motivo (ad esempio malattia, infortunio, ecc.) o senza autorizzazione (ferie), il datore di lavoro si trova spesso in una situazione di emergenza che deve essere gestita nell’immediato. Le mansioni e le attività del collaboratore assente devono essere redistribuite tra gli altri dipendenti oppure occorre reperire tempestivamente eventuali sostituti e provvedere alla loro assunzione e comunicazione obbligatoria (durante il fine settimana tramite il modulo UniUrg).",
        paragraph2:
          "In caso di assenza ingiustificata di un collaboratore, Le consigliamo di contattare immediatamente il/la Suo/a referente della consulenza del personale HGV per concordare insieme le ulteriori modalità di intervento. Nella comunicazione con il collaboratore interessato è importante utilizzare sempre un linguaggio accurato e neutrale, evitando di redigere autonomamente contestazioni disciplinari o altra corrispondenza (ad esempio tramite WhatsApp o email). Si raccomanda inoltre di non reagire ad eventuali provocazioni, poiché ciò potrebbe avere conseguenze negative in caso di controversia.",
        paragraph3:
          "L’assenza ingiustificata del collaboratore deve essere in ogni caso contestata per iscritto. Poiché la contestazione disciplinare deve rispettare determinati requisiti affinché sia valida e corretta sotto il profilo giuridico, il/la Suo/a referente della consulenza del personale HGV la predisporrà individualmente e Le sarà successivamente trasmessa. Affinché la contestazione possa essere notificata in modo conforme alle disposizioni vigenti, è indispensabile disporre dell’indirizzo di residenza corretto del destinatario (anche nel caso di collaboratori residenti all’estero). Mediante la contestazione scritta viene documentata l’assenza ingiustificata e al collaboratore viene concessa la possibilità di fornire una giustificazione, anch’essa in forma scritta.",
        paragraph4:
          "Qualora l’assenza non possa essere giustificata, al collaboratore verrà notificata una successiva comunicazione scritta con la quale sarà informato delle misure disciplinari adottate nei suoi confronti.",
        paragraph5:
          "Il periodo di assenza ingiustificata non viene retribuito: il collaboratore non ha diritto né alla normale retribuzione giornaliera né ad altri elementi retributivi relativi al periodo di assenza. Si raccomanda di informare il collaboratore di tale circostanza anche in forma scritta.",
        paragraph6:
          "Qualora l’assenza ingiustificata del collaboratore si protragga per più di 5 giorni consecutivi, sussistono i presupposti per la risoluzione del rapporto di lavoro.",
        paragraph7:
          "Anche in questo caso, l’assenza deve essere preventivamente contestata mediante una contestazione disciplinare formale. Solo dopo aver accertato l’assenza di valide giustificazioni potrà essere notificata la seconda comunicazione disciplinare, con la quale viene disposta la risoluzione del rapporto di lavoro per licenziamento.",
        paragraph8:
          "Qualora un collaboratore non si presenti al lavoro durante il fine settimana, è generalmente sufficiente contattare il/la proprio/a referente della consulenza del personale HGV il primo giorno lavorativo successivo per esaminare la situazione e concordare le ulteriori azioni da intraprendere.",
      },
      workAccident: {
        title: "Infortunio sul lavoro del dipendente",
        paragraph: (
          <>
            Qualora un collaboratore subisca un infortunio sul lavoro, venga
            successivamente ricoverato in ospedale e presenti il relativo
            certificato di infortunio INAIL, non potrà più svolgere la propria
            attività lavorativa per il periodo indicato nel certificato stesso.
            La preghiamo di trasmettere tempestivamente il certificato di
            infortunio INAIL al/alla Suo/a referente della consulenza del
            personale HGV. La denuncia dell’infortunio sul lavoro verrà quindi
            effettuata da noi entro i termini previsti dalla normativa. Anche
            qualora l’infortunio si verifichi durante il fine settimana o in un
            giorno festivo,{" "}
            <strong>
              la denuncia di infortunio può essere presentata il primo giorno
              lavorativo successivo.
            </strong>
          </>
        ),
        certificateAltText: "Certificato di infortunio",
      },
      illness: {
        title: "Malattia del dipendente",
        paragraph1:
          "Qualora un collaboratore trasmetta o consegni un certificato medico di malattia, non potrà svolgere alcuna attività lavorativa per il periodo indicato nel certificato stesso.",
        paragraph2:
          "La preghiamo di trasmettere al/alla Suo/a referente della consulenza del personale HGV il numero di protocollo del certificato di malattia oppure il certificato stesso.",
        paragraph3:
          "Provvederemo quindi a inserire il periodo di malattia nel registro delle ore del collaboratore.",
        paragraph4:
          "Qualora il collaboratore Le comunichi esclusivamente il numero di protocollo del certificato di malattia e Lei non conosca quindi l’esatto periodo di assenza per malattia, può consultare il certificato completo tramite il seguente link, inserendo il codice fiscale e il numero di protocollo.",
        inpsLinkLabel: "Attestati malattia – Consultazione (inps.it)",
        sickNoteAltText: "Certificato di malattia",
      },
      individualService: {
        title: "Servizio di reperibilità individuale",
        paragraph1: (
          <>
            Qualora le domande e risposte sopra riportate non consentano di
            risolvere il Suo caso specifico e Lei necessiti comunque di
            un’informazione urgente, può cliccare sul pulsante{" "}
            <strong>“Segnala un’emergenza”</strong> riportato di seguito.
          </>
        ),
        paragraph2:
          "In questo modo sarà contattato/a tempestivamente dal/dalla responsabile di area della consulenza del personale HGV competente per il Suo caso.",
        paragraph3: "Il servizio è attivo nei seguenti orari:",
        summerHours:
          "Durante il periodo dell’orario estivo, dal 6 luglio 2026 al 28 agosto 2026, il servizio è attivo dal lunedì al venerdì dalle ore 14:00 alle ore 17:00.",
        holidayHoursHeading:
          "Nei seguenti giorni dalle ore 08:00 alle ore 17:00:",
        holidays: [
          "Lunedì di Pasqua (Pasquetta), 6 aprile 2026",
          "Festa del Lavoro, 1° maggio 2026",
          "Lunedì di Pentecoste, 25 maggio 2026",
          "Natale, 25 dicembre 2026",
        ],
        warning:
          "ATTENZIONE: per l’attivazione del servizio di reperibilità individuale nei giorni festivi è previsto un costo di gestione pari a 130,00 euro.",
      },
    },
    reportEmergency: "Segnala un’emergenza",
    serviceMomentarilyNotActive:
      "Il servizio di reperibilità non è attualmente attivo.",
    initError:
      "Si è verificato un errore durante l’inizializzazione della segnalazione di emergenza.",
    modal: {
      submit: "Effettua una segnalazione di emergenza",
      cancel: "Annulla",
      company: "Azienda",
      phone: "Numero di telefono",
      description: "Breve descrizione dell’accaduto / della richiesta",
      acceptTerms:
        "Dichiaro di accettare l’attivazione del servizio di reperibilità individuale, per il quale nei giorni festivi sarà addebitato un costo di gestione pari a 130,00 euro.",
      acceptTermsRequired: "Se desidera proseguire, selezioni questa casella.",
      success: "Segnalazione di emergenza inviata con successo.",
      error:
        "Si è verificato un errore durante l’invio della segnalazione di emergenza.",
    },
  },
  arbeitAufAbruf: {
    title:
      "Lavoro a chiamata – Comunicazione preventiva della chiamata al lavoro",
    paragraph1: (
      formLink: ReactNode,
      email: ReactNode,
      instructionsLink: ReactNode,
    ) => (
      <>
        Per ogni lavoratore assunto con contratto di lavoro a chiamata, prima
        dell’inizio della prestazione lavorativa deve essere effettuata
        un’ulteriore comunicazione al Ministero del Lavoro. Tale comunicazione
        deve essere inviata direttamente dal datore di lavoro, tramite il{" "}
        {formLink}, sotto forma di file XML all’indirizzo email {email}. Le
        relative istruzioni sono disponibili {instructionsLink}.
      </>
    ),
    paragraph1FormLinkLabel: "modulo online “UNI_INTERMITTENTE”",
    paragraph1InstructionsLinkLabel: "qui",
    paragraph2:
      "Il contratto di lavoro a chiamata è stato stipulato il 1° agosto e il/la lavoratore/trice risulta regolarmente assunto/a e comunicato/a agli uffici competenti. Il 22 agosto il/la lavoratore/trice viene chiamato/a a prestare servizio a partire dalle ore 11:00, mentre il 28 agosto viene chiamato/a a partire dalle ore 19:00. In questo caso, ciascuna giornata di lavoro deve essere comunicata separatamente tramite il modulo online.",
    clarification: {
      label: "Precisazione",
      description:
        "La comunicazione relativa all’inizio della prestazione lavorativa del/della lavoratore/trice deve essere effettuata immediatamente prima dell’inizio dell’attività. Ad esempio, se l’orario di inizio è previsto per le ore 11:00, la comunicazione deve essere trasmessa entro e non oltre le ore 10:59.",
    },
    cancellation: {
      label: "Annullamento",
      description:
        "Qualora il/la lavoratore/trice annulli la propria disponibilità con breve preavviso e la comunicazione sia già stata trasmessa, l’annullamento deve essere effettuato immediatamente nello stesso giorno previsto per la prestazione lavorativa (e non il giorno successivo).",
    },
    penalties: {
      label: "Sanzioni in caso di omissione",
      description:
        "Per ogni mancata comunicazione dell’inizio della prestazione lavorativa oppure della mancata prestazione da parte del/della lavoratore/trice, la legge prevede l’applicazione di sanzioni amministrative comprese tra 400 e 2.400 euro.",
    },
    presenceRegister: {
      label: "Registro delle presenze",
      description:
        "Le ore effettivamente lavorate devono essere registrate mensilmente dal datore di lavoro nel registro delle presenze.",
    },
  },
  joinJobOffers: {
    title: "Guida: creare un annuncio di lavoro su onboard",
    sidebarScreenshotAltText:
      "Screenshot della barra laterale “Recruiting” con la voce di menu “Annunci di lavoro” e l’opzione “Nuovo” selezionata",
    formScreenshotAltText:
      "Screenshot del modulo “Informazioni di base” con l’indicazione “Cliccare sul mappamondo”",
    steps: {
      new: "Nella categoria a sinistra “Annunci di lavoro” cliccare su “Nuovo”.",
      basicInfo: {
        label: <strong>Informazioni di base:</strong>,
        items: [
          "Lingue: selezionare “tutte”, in modo che l’annuncio possa essere visualizzato sul sito web in tutte e 3 le lingue.",
          "Numero di riferimento: è visibile solo internamente – utile se volete ordinare i vostri annunci (ad es. numerando tutte le posizioni o contrassegnandole con una lettera specifica).",
          "Sede: selezionare la vostra sede.",
          "Reparti: quale area dell’azienda riguarda la posizione aperta? (cucina, sala, …)",
          "Titolo: ad es. Chef de Rang, apprendista cucina → IMPORTANTE: cliccare sul mappamondo, poi su “traduci tutto”, in modo che il titolo dell’annuncio venga tradotto automaticamente in tutte le lingue.",
        ],
      },
      content: {
        label: (
          <>
            <strong>Contenuto dell’annuncio</strong> (seguire lo schema modello,
            che si trova sotto “Bozza”) → IMPORTANTE: anche qui cliccare sul
            mappamondo, poi su “traduci tutto”:
          </>
        ),
        items: [
          "Breve introduzione: di quale posizione si tratta.",
          "Cosa deve portare la persona = quali competenze deve avere.",
          "Descrizione delle mansioni: cosa c’è da fare esattamente.",
          "IMPORTANTE: La persona di contatto dell’azienda comunicata viene visualizzata automaticamente in ogni annuncio; qui si potrebbe ad es. inserire il link al sito web oppure aggiungere un ulteriore contatto generale dell’azienda.",
        ],
        note: "Nota generale: prevedere un numero sufficiente di paragrafi tra i contenuti.",
      },
      employmentType:
        "Tipo di contratto: selezionare l’opzione corrispondente + selezionare la data di inizio.",
      salary:
        "Indicare la retribuzione (è possibile indicare anche una fascia retributiva) – facoltativo.",
      autoReply:
        "Risposta email automatica: lasciare la preselezione “Risposta standard”.",
      beforePublishing: {
        label: "Poco prima della pubblicazione:",
        items: [
          "Scegliere se l’annuncio deve essere pubblicato subito.",
          "Non lasciare la durata illimitata, altrimenti nel sistema si accumulano molti annunci scaduti = meglio limitarla a due settimane e, se necessario, prolungarla.",
        ],
      },
    },
    duplicateHint:
      "Per tutti i futuri annunci: una volta creato un annuncio secondo lo schema corretto, è sufficiente “duplicare” la posizione.",
  },
  dac7: {
    backLink: "Torna al Web Marketing",
    notReportable:
      "L’azienda selezionata non è tenuta a effettuare la comunicazione obbligatoria.",
    requiredOrDash:
      "Il campo deve contenere un valore. Se non è disponibile alcun valore, inserire un trattino (-).",
    seller: {
      title: "Informazioni sul venditore",
      classification: {
        label: "Classificazione fiscale",
        helpText:
          "Qualora tali informazioni non siano aggiornate, La preghiamo di contattarci.",
        company: "Azienda",
        soleProprietorship: "Impresa individuale",
        privatePerson: "Persona fisica",
      },
      companyName: {
        label: "Denominazione legale dell’azienda",
        helpText:
          "Questa rappresenta la denominazione giuridicamente valida della Sua azienda. Qualora i dati non fossero aggiornati, La invitiamo a contattarci.",
      },
      firstName: {
        label: "Nome",
        helpText:
          "Questo è il Suo nome ufficiale. Qualora non fosse corretto, La invitiamo a contattarci.",
      },
      lastName: {
        label: "Cognome",
        helpText:
          "Questo è il Suo cognome legalmente registrato. Qualora non fosse corretto, La preghiamo di contattarci.",
      },
      birthDate: "Data di nascita",
      pec: "Indirizzo PEC",
      taxId: {
        label: "Codice fiscale",
        helpText:
          "La preghiamo di indicare il Suo codice fiscale e il Paese di rilascio.",
      },
      vatId: {
        label: "Partita IVA",
        helpText:
          "Indichi la Sua partita IVA comprensiva del prefisso nazionale a due lettere del Paese di appartenenza.",
      },
      reaNumber: {
        label:
          "Numero REA (Numero di iscrizione al Repertorio Economico Amministrativo)",
        helpText: (link: ReactNode) => (
          <>
            Può trovare il numero REA nella prima pagina della visura camerale.{" "}
            {link} è indicato dalla freccia rossa.
          </>
        ),
        helpTextLinkLabel: "Nell’esempio qui riportato",
        instructionsPdfTitle: "Istruzioni per trovare il numero REA",
      },
      address: "Indirizzo",
      street: "Strada",
      buildingNumber: "Numero",
      postalCode: "CAP",
      municipality: "Comune",
      province: "Provincia",
      country: "Paese",
      bankData: "Dati bancari",
      accountOwner: "Intestatario del conto bancario",
      iban: "IBAN",
      permanentEstablishments: {
        title: "Sedi",
        description:
          "Selezioni i Paesi nei quali concede in locazione strutture operative (situate all’interno dell’Unione Europea).",
        minOneCountry: "È necessario selezionare almeno un Paese.",
      },
    },
    companies: {
      title: "Dati delle sedi operative",
      description:
        "Per ogni unità immobiliare commercializzata tramite il portale “Booking Alto Adige”, La preghiamo di indicare i seguenti dati.",
      notReportable:
        "Questa sede operativa non è soggetta all’obbligo di comunicazione.",
      address: "Indirizzo",
      street: "Strada",
      buildingNumber: "Numero",
      postalCode: "CAP",
      municipality: "Comune",
      province: "Provincia",
      country: "Paese",
      landRegistration: {
        title: "Dati catastali",
        description: (link: ReactNode) => (
          <>
            {link} sono disponibili le istruzioni per individuare i dati
            richiesti nella visura catastale della relativa unità immobiliare.
            Se per il foglio o per l’unità edilizia non è indicato alcun valore,
            inserisca un trattino (-) nel relativo campo.
          </>
        ),
        descriptionLinkLabel: "In questo documento",
        instructionsPdfTitle: "Dati catastali per la comunicazione DAC7",
        municipalityCode: {
          title: "Codice del comune",
          subtitle: "Codice catastale del comune",
        },
        sheet: { title: "Foglio", subtitle: "Foglio" },
        parcel: { title: "Particella", subtitle: "Particella" },
        subunit: {
          title: "Subalterno",
          subtitle: "Sub. o subalterno",
        },
        addEntry: "Aggiungi un elemento",
      },
    },
    errors: {
      loadError:
        "Si è verificato un errore durante il caricamento dei dati associativi.",
      configIncomplete: {
        line1:
          "La configurazione del socio selezionato non è stata ancora completata.",
        line2: "La preghiamo di rivolgersi al reparto Web Marketing dell’HGV.",
      },
    },
  },
};

export default i18n;
